import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, User as UserIcon, Phone, ShieldCheck, Clock, Star, Gift, MapPin } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ASSETS } from '../mock/data';

const Signup = ({ cleanerMode = false }) => {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { signup } = useApp();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', referral_code: '', district: '' });
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const ref = params.get('ref');
    if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
    // Fetch approved districts
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/franchises/districts`)
      .then((r) => r.json())
      .then((d) => setDistricts(d.districts || []))
      .catch(() => {});
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const u = await signup({
        ...form,
        referral_code: form.referral_code || undefined,
        district: form.district || undefined,
        roleWanted: cleanerMode ? 'cleaner' : 'customer',
      });
      nav(u.role === 'cleaner' ? '/cleaner/dashboard' : '/home');
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFF7C2 0%, #FFEB99 100%)' }}>
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#FFE066] opacity-70" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#F5E663] opacity-60" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <button onClick={() => nav('/')} className="self-start">
            <img src={ASSETS.logo} alt="Clean24" className="h-16" />
          </button>

          <div>
            <img src={cleanerMode ? ASSETS.staff : ASSETS.team} alt="" className="rounded-2xl shadow-xl h-72 w-full object-cover" />
            <h2 className="mt-6 text-3xl font-extrabold text-neutral-900">Join Clean24 today</h2>
            <p className="mt-2 text-neutral-700">{cleanerMode ? 'Sign up as a cleaner and start earning.' : 'Book verified cleaners in 60 seconds.'}</p>
            <div className="mt-4 flex items-center gap-4 text-xs text-neutral-700">
              <div className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#2E7D32]" /> Secure</div>
              <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#2E7D32]" /> Quick booking</div>
              <div className="flex items-center gap-1"><Star className="w-4 h-4 text-[#F5B301] fill-[#F5B301]" /> Trusted</div>
            </div>
          </div>

          <p className="text-xs text-neutral-600">© 2026 Clean24. All rights reserved.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 md:px-8 py-8">
        <div className="w-full max-w-md">
          <button onClick={() => nav(-1)} className="md:hidden w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="md:hidden flex flex-col items-center mb-6">
            <img src={ASSETS.logo} alt="Clean24" className="h-16" />
          </div>

          <h1 className="text-2xl md:text-3xl font-extrabold text-neutral-900">Create account</h1>
          <p className="text-sm text-neutral-600 mt-1">{cleanerMode ? 'Join as a Clean24 cleaner' : 'Book verified cleaners in 60 seconds'}</p>

          {err && <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{err}</div>}

          <form onSubmit={submit} className="mt-6 space-y-3">
            <IconInput icon={UserIcon} placeholder="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <IconInput icon={Mail} type="email" placeholder="Email address" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <IconInput icon={Phone} type="tel" placeholder="Phone number" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <IconInput icon={Lock} type="password" placeholder="Create password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
            {!cleanerMode && (
              <>
                <IconInput
                  icon={Gift}
                  placeholder="Referral code (optional)"
                  value={form.referral_code}
                  required={false}
                  data-testid="signup-referral-code"
                  onChange={(v) => setForm({ ...form, referral_code: v.toUpperCase() })}
                />
                <div className="relative">
                  <MapPin className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <select
                    required
                    data-testid="signup-district"
                    value={form.district}
                    onChange={(e) => setForm({ ...form, district: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 bg-white focus:outline-none focus:border-[#5AA02C] focus:ring-2 focus:ring-[#5AA02C]/20 text-sm appearance-none">
                    <option value="">Select your district</option>
                    {districts.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <p className="text-[11px] text-neutral-500 -mt-1 pl-1">Your Clean24 franchise will be assigned based on this.</p>
              </>
            )}

            <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-70 text-white font-semibold transition-colors">
              {loading ? 'Creating account...' : 'Sign up'}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-neutral-600">
            Already have an account? <Link to={cleanerMode ? '/cleaner/login' : '/login'} className="text-[#5AA02C] font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const IconInput = ({ icon: Icon, required = true, ...props }) => (
  <div className="relative">
    <Icon className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
    <input required={required} {...props} onChange={(e) => props.onChange(e.target.value)}
      className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 bg-white focus:outline-none focus:border-[#5AA02C] focus:ring-2 focus:ring-[#5AA02C]/20 text-sm" />
  </div>
);

export default Signup;
